import { useEffect, useState } from "react";

// Code block style using CSS variables (theme-aware)
const getCodeBlockStyle = () => ({
  backgroundColor: 'var(--formulas-bg)',
  color: 'var(--text-primary)',
  borderRadius: '8px',
  padding: '16px',
  overflow: 'auto',
  fontSize: '14px',
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, source-code-pro, monospace',
  lineHeight: '1.5',
  whiteSpace: 'pre' as const,
  wordBreak: 'normal' as const,
  wordWrap: 'normal' as const,
  tabSize: 2,
});

// Helper function to unescape HTML entities
const unescapeHtml = (text: string): string => {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
};

export // Lightweight syntax highlighter component
const CodeHighlighter: React.FC<{ 
  code: string; 
  language: string; 
  showLineNumbers?: boolean;
}> = ({ code, language, showLineNumbers = false }) => {
  const [highlighted, setHighlighted] = useState<string>('');
  const [prismLoaded, setPrismLoaded] = useState(false);
  const [highlightingReady, setHighlightingReady] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(localStorage.getItem('theme') || 'dark');

  useEffect(() => {
    // Update theme when it changes
    const handleThemeChange = () => {
      const newTheme = localStorage.getItem('theme') || 'dark';
      setCurrentTheme(newTheme);
    };

    document.addEventListener('themeChanged', handleThemeChange);
    return () => document.removeEventListener('themeChanged', handleThemeChange);
  }, []);

  useEffect(() => {
    // Load Prism.js if not already loaded
    if (!window.Prism) {
      // Load Prism core
      const prismScript = document.createElement('script');
      prismScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/prism.min.js';
      prismScript.onload = () => {
        // Load language components
        const languages = ['javascript', 'typescript', 'csharp', 'sql', 'python', 'jsx', 'tsx'];
        let loadedCount = 0;
        
        languages.forEach(lang => {
          const langScript = document.createElement('script');
          
          langScript.src = `https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/components/prism-${lang}.min.js`;
          langScript.onload = () => {
            loadedCount++;
            if (loadedCount === languages.length) {
              setPrismLoaded(true);
            }
          };
          langScript.onerror = () => {
            loadedCount++;
            if (loadedCount === languages.length) {
              setPrismLoaded(true);
            }
          };
          document.head.appendChild(langScript);
        });
      };
      document.head.appendChild(prismScript);
    } else {
      setPrismLoaded(true);
    }
  }, []);

  useEffect(() => {
    // Load appropriate CSS theme
    const loadPrismCSS = () => {
      // Remove existing Prism CSS
      const existingCSS = document.querySelector('link[href*="prism"]');
      if (existingCSS) {
        existingCSS.remove();
      }

      // Only load CDN CSS for light and dark themes
      if (currentTheme !== 'blonde') {
        const prismCSS = document.createElement('link');
        prismCSS.rel = 'stylesheet';
        prismCSS.href = currentTheme === 'light' 
          ? 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism.min.css'
          : 'https://cdnjs.cloudflare.com/ajax/libs/prism/1.29.0/themes/prism-tomorrow.min.css';
        document.head.appendChild(prismCSS);
      }
    };

    loadPrismCSS();
  }, [currentTheme]);

  useEffect(() => {
    if (prismLoaded && window.Prism) {
      try {
        // Unescape the code before highlighting
        const unescapedCode = unescapeHtml(code);
        
        // Map common language aliases
        let prismLanguage = language.toLowerCase();
        const languageMap: { [key: string]: string } = {
          'js': 'javascript',
          'ts': 'typescript',
          'tsx': 'tsx',
          'jsx': 'jsx',
          'c#': 'csharp',
          'csharp': 'csharp',
          'py': 'python',
          'react': 'jsx'
        };
        
        if (languageMap[prismLanguage]) {
          prismLanguage = languageMap[prismLanguage];
        }

        // Check if language is supported
        if (window.Prism.languages[prismLanguage]) {
          const highlightedCode = window.Prism.highlight(
            unescapedCode,
            window.Prism.languages[prismLanguage],
            prismLanguage
          );
          setHighlighted(highlightedCode);
          setHighlightingReady(true);
        } else {
          setHighlighted(unescapedCode); // Fallback to plain text
          setHighlightingReady(true);
        }
      } catch (error) {
        console.warn('Prism highlighting failed:', error);
        setHighlighted(unescapeHtml(code));
        setHighlightingReady(true);
      }
    } else {
      setHighlightingReady(false);
    }
  }, [code, language, prismLoaded]);

  const codeBlockStyle = getCodeBlockStyle();

  // Custom syntax highlighting colors for blonde theme (uses CSS vars)
  const blondeTokenStyles = currentTheme === 'blonde' ? {
    '.token.comment, .token.prolog, .token.doctype, .token.cdata': { 
      color: 'var(--text-muted)', 
      fontStyle: 'italic' 
    },
    '.token.punctuation': { 
      color: 'var(--text-primary)' 
    },
    '.token.property, .token.tag, .token.constant, .token.symbol, .token.deleted': { 
      color: 'var(--accent-color)' 
    },
    '.token.boolean, .token.number': { 
      color: 'var(--warning-color)' 
    },
    '.token.selector, .token.attr-name, .token.string, .token.char, .token.builtin, .token.inserted': { 
      color: 'var(--success-color)' 
    },
    '.token.operator, .token.entity, .token.url, .language-css .token.string, .style .token.string, .token.variable': { 
      color: 'var(--text-secondary)' 
    },
    '.token.atrule, .token.attr-value, .token.function, .token.class-name': { 
      color: 'var(--accent-color)' 
    },
    '.token.keyword': { 
      color: 'var(--accent-hover)',
      fontWeight: 'bold' 
    },
    '.token.regex, .token.important': { 
      color: 'var(--warning-color)',
      fontWeight: 'bold' 
    },
    '.token.important, .token.bold': { 
      fontWeight: 'bold' 
    },
    '.token.italic': { 
      fontStyle: 'italic' 
    }
  } : {};

  if (!prismLoaded || !highlightingReady) {
    // Unescape for display even when Prism isn't loaded
    const unescapedCode = unescapeHtml(code);
    return (
      <div className="code-block">
        <pre style={codeBlockStyle}>
          <code>{unescapedCode}</code>
        </pre>
      </div>
    );
  }

  const unescapedCode = unescapeHtml(code);
  const lines = unescapedCode.split('\n');
  const shouldShowLineNumbers = showLineNumbers && lines.length > 1;

  return (
    <>
      {/* Inline styles for blonde theme only */}
      {currentTheme === 'blonde' && (
        <style>{`
          ${Object.entries(blondeTokenStyles).map(([selector, styles]) => 
            `${selector} { ${Object.entries(styles as Record<string, string>).map(([prop, value]) => `${prop}: ${value}`).join('; ')} }`
          ).join('\n')}
        `}</style>
      )}
      <pre style={codeBlockStyle}>
        {shouldShowLineNumbers && (
          <div style={{
            position: 'absolute',
            left: '0',
            top: '16px',
            bottom: '16px',
            width: '40px',
            color: 'var(--text-muted)',
            fontSize: '12px',
            lineHeight: '1.5',
            textAlign: 'right',
            paddingRight: '8px',
            userSelect: 'none',
            fontFamily: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            paddingTop: '0',
          }}>
            {lines.map((_, index) => (
              <span key={index} style={{ minHeight: '21px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                {index + 1}
              </span>
            ))}
          </div>
        )}
        <code 
          className={`language-${language}`}
          style={{ 
            paddingLeft: shouldShowLineNumbers ? '48px' : '0',
            display: 'block'
          }}
          dangerouslySetInnerHTML={{ __html: highlighted || unescapedCode }}
        />
      </pre>
    </>
  );
};